import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, DollarSign, Users, TrendingUp, Mail, Webhook,
  BookOpen, Loader2, RefreshCw, ArrowRight, CheckCircle2, XCircle, Clock,
  Sparkles, PartyPopper, UserPlus, Trophy,
} from "lucide-react";
import MagicRecoveryCard from "./MagicRecoveryCard";
import MagicFunnelCard from "./MagicFunnelCard";
import TrashStatsCard from "./TrashStatsCard";
import RecordingStudioCard from "./RecordingStudioCard";
import RentCastUsageTile from "./RentCastUsageTile";

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [sparks, setSparks] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/overview/sparklines?days=14"),
      ]);
      setData(r1.data);
      setSparks(r2.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !data) {
    return (
      <div className="card p-12 text-center text-[var(--ink-2)]" data-testid="admin-overview-loading">
        <Loader2 size={20} className="animate-spin inline" /> Loading overview…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-overview">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide flex items-center gap-1.5">
            <LayoutDashboard size={12} /> Admin
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Overview</h1>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Your morning-coffee snapshot — top KPIs from every system in one place.
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={refresh} data-testid="admin-overview-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Hero strip — the 4 most important numbers in big type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <HeroStat
          label="MRR"
          value={fmtMoney(data.revenue.mrr_usd * 100)}
          sub={`${data.revenue.active_subscriptions} active · ${data.revenue.trialing} trial`}
          icon={DollarSign}
          accent="emerald"
          to="/app/admin/revenue"
          spark={sparks?.new_paying}
          sparkLabel="new paying"
          testid="overview-hero-mrr"
        />
        <HeroStat
          label="Total Users"
          value={data.users.total.toLocaleString()}
          sub={`+${data.users.new_7d} this week`}
          icon={Users}
          accent="sky"
          to="/app/admin/users"
          spark={sparks?.new_users}
          sparkLabel="new users"
          testid="overview-hero-users"
        />
        <HeroStat
          label="Pipeline Value"
          value={fmtMoney(data.pipeline.value_usd * 100)}
          sub={`${data.pipeline.active_deals.toLocaleString()} active deals`}
          icon={TrendingUp}
          accent="amber"
          spark={sparks?.new_leads}
          sparkLabel="new leads"
          testid="overview-hero-pipeline"
        />
        <HeroStat
          label="Won (30d)"
          value={fmtMoney(data.pipeline.won_value_30d_usd * 100)}
          sub={`${data.pipeline.won_count_30d} deals closed`}
          icon={Trophy}
          accent="violet"
          spark={sparks?.won_deals}
          sparkLabel="won/day"
          testid="overview-hero-won"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue panel */}
        <Panel
          title="Revenue"
          icon={DollarSign}
          link="/app/admin/revenue"
          linkLabel="Open Revenue"
          testid="overview-panel-revenue"
        >
          <Row label="MRR" value={fmtMoney(data.revenue.mrr_usd * 100)} bold />
          <Row label="ARR" value={fmtMoney(data.revenue.arr_usd * 100)} />
          <Row label="Active subs" value={data.revenue.active_subscriptions} />
          <Row label="Trialing" value={data.revenue.trialing} />
          <Row label="Cancel pending" value={data.revenue.cancel_pending} accent={data.revenue.cancel_pending > 0 ? "rose" : null} />
          <Row label="Churned (30d)" value={data.revenue.churned_last_30d} accent={data.revenue.churned_last_30d > 0 ? "rose" : null} />
        </Panel>

        {/* Email Ops panel */}
        <Panel
          title="Email Ops · 7d"
          icon={Mail}
          link="/app/admin/email"
          linkLabel="Open Email Ops"
          testid="overview-panel-email"
        >
          <Row label="Sent" value={data.email.sent_7d.toLocaleString()} bold />
          <Row label="Delivered" value={data.email.delivered_7d.toLocaleString()} />
          <Row label="Errored" value={data.email.errored_7d.toLocaleString()} accent={data.email.errored_7d > 0 ? "amber" : null} />
          <Row label="Open rate" value={`${data.email.open_rate}%`} />
          <Row label="Click rate" value={`${data.email.click_rate}%`} />
        </Panel>

        {/* Webhook Health panel */}
        <Panel
          title="Stripe Webhooks · 24h"
          icon={Webhook}
          link="/app/admin/revenue"
          linkLabel="Open Webhook Health"
          testid="overview-panel-webhook"
        >
          <div className="mb-2">
            <WebhookStatusBadge status={data.webhook_health.status} />
          </div>
          <Row label="Success" value={data.webhook_health.success_24h} accent="emerald" />
          <Row label="Failed" value={data.webhook_health.failure_24h} accent={data.webhook_health.failure_24h > 0 ? "rose" : null} />
          {data.webhook_health.last_failure && (
            <div className="mt-2 p-2 rounded bg-rose-50 border border-rose-200 text-[10px] font-mono break-words text-rose-800">
              <div className="font-bold mb-0.5">{data.webhook_health.last_failure.event_type}</div>
              {data.webhook_health.last_failure.error}
            </div>
          )}
        </Panel>

        {/* Resources engagement */}
        <Panel
          title="Resources · 7d"
          icon={BookOpen}
          link="/app/admin/resources"
          linkLabel="Open Resources"
          testid="overview-panel-resources"
        >
          <Row label="Resource views" value={data.resources.views_7d.toLocaleString()} bold />
          <Row label="Tracks completed (7d)" value={data.resources.track_completions_7d} accent={data.resources.track_completions_7d > 0 ? "emerald" : null} />
          <Row label="Tracks completed (all-time)" value={data.resources.track_completions_total} />
          {data.resources.top_resource && (
            <Link
              to={`/app/resources/${data.resources.top_resource.slug}`}
              className="mt-2 block p-2 rounded bg-slate-50 hover:bg-slate-100 text-xs"
              data-testid="overview-top-resource"
            >
              <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] mb-0.5">
                Top resource overall
              </div>
              <div className="font-semibold line-clamp-1">{data.resources.top_resource.title}</div>
              <div className="text-[10px] text-[var(--ink-3)] mt-0.5">
                {data.resources.top_resource.view_count} total views
              </div>
            </Link>
          )}
        </Panel>

        {/* Users panel */}
        <Panel
          title="Users"
          icon={Users}
          link="/app/admin"
          linkLabel="Open Users"
          testid="overview-panel-users"
        >
          <Row label="Total" value={data.users.total.toLocaleString()} bold />
          <Row label="Paying" value={data.users.paying} accent="emerald" />
          <Row label="Free" value={data.users.free.toLocaleString()} />
          <Row label="New (7d)" value={`+${data.users.new_7d}`} accent="sky" />
          <Row label="New (30d)" value={`+${data.users.new_30d}`} />
          <Row label="Active (7d)" value={data.users.active_7d} />
        </Panel>

        {/* Recent activity ticker */}
        <Panel
          title="Recent Activity"
          icon={Sparkles}
          testid="overview-panel-activity"
        >
          {data.recent_activity.length === 0 ? (
            <div className="text-xs text-[var(--ink-3)] py-4 text-center">No recent activity.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.recent_activity.map((ev, i) => (
                <li key={i} className="flex items-start gap-2 text-xs" data-testid="overview-activity-row">
                  <ActivityIcon kind={ev.kind} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{ev.label}</div>
                    <div className="text-[10px] text-[var(--ink-3)] tabular-nums">{relTime(ev.at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <MagicFunnelCard />

      <RentCastUsageTile />

      <MagicRecoveryCard />

      <TrashStatsCard />

      <RecordingStudioCard />


      <div className="text-[10px] text-[var(--ink-3)] text-center pt-2">
        Generated {relTime(data.generated_at)} · click any panel header to drill in.
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function HeroStat({ label, value, sub, icon: Icon, accent, to, spark, sparkLabel, testid }) {
  const palette = {
    emerald: { tone: "bg-emerald-50 text-emerald-700", line: "#10B981" },
    sky:     { tone: "bg-sky-50 text-sky-700",         line: "#0EA5E9" },
    amber:   { tone: "bg-amber-50 text-amber-700",     line: "#F59E0B" },
    violet:  { tone: "bg-violet-50 text-violet-700",   line: "#8B5CF6" },
  }[accent] || { tone: "bg-slate-50 text-slate-700", line: "#64748B" };
  const inner = (
    <div className="card p-4 h-full" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-bold">{label}</div>
        <div className={"w-8 h-8 rounded-lg flex items-center justify-center " + palette.tone}>
          <Icon size={14} />
        </div>
      </div>
      <div className="font-display font-black text-3xl tabular-nums leading-none">{value}</div>
      <div className="text-xs text-[var(--ink-2)] mt-1">{sub}</div>
      {spark && spark.length > 1 && (
        <div className="mt-3 pt-2 border-t border-slate-100" data-testid={`${testid}-spark`}>
          <Sparkline values={spark} color={palette.line} />
          <div className="text-[9px] uppercase tracking-wide text-[var(--ink-3)] mt-1 flex items-center justify-between">
            <span>14d · {sparkLabel}</span>
            <span className="tabular-nums">total {spark.reduce((a, b) => a + b, 0).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
  return to ? <Link to={to} className="hover:shadow-lg transition-shadow block">{inner}</Link> : inner;
}

function Sparkline({ values, color }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 1);
  const w = 100;
  const h = 24;
  const step = w / (values.length - 1);
  const points = values.map((v, i) => `${i * step},${h - (v / max) * h}`).join(" ");
  const last = values[values.length - 1];
  const lastY = h - (last / max) * h;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-6 block" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={w} cy={lastY} r="2" fill={color} />
    </svg>
  );
}

function Panel({ title, icon: Icon, link, linkLabel, children, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 pb-2 border-b">
        <div className="text-sm font-display font-bold flex items-center gap-1.5">
          <Icon size={14} className="text-[var(--ink-2)]" /> {title}
        </div>
        {link && (
          <Link to={link} className="text-[10px] uppercase tracking-wide font-bold text-[var(--primary)] hover:underline inline-flex items-center gap-0.5">
            {linkLabel} <ArrowRight size={10} />
          </Link>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, bold = false, accent = null }) {
  const accentClass = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    amber: "text-amber-700",
    sky: "text-sky-700",
  }[accent] || "";
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <div className="text-[var(--ink-2)]">{label}</div>
      <div className={"tabular-nums " + (bold ? "font-display font-black text-base" : "font-semibold") + " " + accentClass}>
        {value}
      </div>
    </div>
  );
}

function WebhookStatusBadge({ status }) {
  const map = {
    healthy:  { label: "Healthy",  Icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700" },
    degraded: { label: "Degraded", Icon: XCircle,      tone: "bg-rose-100 text-rose-700" },
    idle:     { label: "Idle",     Icon: Clock,        tone: "bg-slate-100 text-slate-700" },
  };
  const { label, Icon, tone } = map[status] || map.idle;
  return (
    <span className={"px-2 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 " + tone}>
      <Icon size={11} /> {label}
    </span>
  );
}

function ActivityIcon({ kind }) {
  if (kind === "track_completed") return <PartyPopper size={12} className="text-amber-600 shrink-0 mt-0.5" />;
  if (kind === "user_signup")     return <UserPlus size={12} className="text-sky-600 shrink-0 mt-0.5" />;
  return <Sparkles size={12} className="text-slate-500 shrink-0 mt-0.5" />;
}

function relTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
